/**
 * RESHAPING A LINE, MIRO'S WAY — owner, 2026-11-04: whiteboard 裡面 object 連接的線條能操作的空間太少了. 很不方便, 你去 Miro
 * 仔細研究他的 edge 是怎麼設計的. 然後把它原封不動的搬到我們的 app 裡面來.
 *
 * What Miro does with a chosen line, and so what this does:
 *  - an elbow: every segment has a dot at its middle, and dragging the dot — or the segment itself — slides that segment
 *    sideways. A segment at an end gets a short stub first, so the end still leaves its side straight (the owner's own
 *    drawing: a level link dragged up into a step).
 *  - a straight or curved line: a dot at the middle of each piece; dragging it makes a new bend there, and the bends are
 *    dots of their own to drag again. A bend dragged back into line with its neighbours goes away.
 *  - the words on a line slide along it.
 *
 * Everything here is pure: the canvas measures the drag, this says what the line's `pts` become.
 */
import { CORNER, HOP, STUB, dist, flatPts, isAttached, samples, unit, viaOf, type El, type LineGeom, type Pt } from './model'

/** A handle on a chosen line: a bend to drag (`pt`), the middle of a piece to bend there (`mid`), an elbow's segment (`seg`). */
export type LineHandle = { kind: 'pt' | 'mid' | 'seg'; i: number; at: Pt }

/**
 * The handles a chosen line shows. `minLen`: a piece shorter than this (board pixels) gets no dot of its own — it would
 * sit on top of the ends' circles.
 */
export function lineHandles(line: El, g: LineGeom, minLen: number): LineHandle[] {
  const out: LineHandle[] = []
  if (g.elbow) {
    for (let i = 0; i + 1 < g.pts.length; i++) {
      const p = g.pts[i]!
      const q = g.pts[i + 1]!
      if (dist(p, q) < minLen) continue
      out.push({ kind: 'seg', i, at: { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 } })
    }
    return out
  }
  const via = viaOf(line)
  via.forEach((at, i) => out.push({ kind: 'pt', i, at }))
  const nodes = g.curve ? g.pts.filter((_, k) => k % 3 === 0) : g.pts
  for (let i = 0; i + 1 < nodes.length; i++) {
    const at = g.curve ? curveMid(g, i) : { x: (nodes[i]!.x + nodes[i + 1]!.x) / 2, y: (nodes[i]!.y + nodes[i + 1]!.y) / 2 }
    if (dist(nodes[i]!, nodes[i + 1]!) < minLen) continue
    out.push({ kind: 'mid', i, at })
  }
  return out
}

function curveMid(g: LineGeom, piece: number): Pt {
  const [p0, c1, c2, p3] = g.pts.slice(piece * 3, piece * 3 + 4) as [Pt, Pt, Pt, Pt]
  const t = 0.5
  const m = (a: number, b: number, c: number, d: number) => (1 - t) ** 3 * a + 3 * (1 - t) ** 2 * t * b + 3 * (1 - t) * t ** 2 * c + t ** 3 * d
  return { x: m(p0.x, c1.x, c2.x, p3.x), y: m(p0.y, c1.y, c2.y, p3.y) }
}

/** Is this piece of an elbow level (it slides up and down) — else upright (it slides sideways)? */
export function isLevel(p: Pt, q: Pt): boolean {
  return Math.abs(p.y - q.y) <= Math.abs(p.x - q.x)
}

/**
 * An elbow with segment `k` of its drawn corners `pts` slid by `by` across its own direction; its new `pts` (the
 * corners between its ends). A segment that starts or ends at an end keeps a stub of it, so that end still leaves its
 * side straight: the slid part is joined to the stub by a short upright (or level) piece — Miro's step.
 */
export function slideSegment(pts: Pt[], k: number, by: number): number[] {
  const Q = pts.map((q) => ({ ...q }))
  const n = Q.length - 1
  if (k < 0 || k >= n) return flatPts(Q.slice(1, -1))
  let s = k
  const along = (from: Pt, to: Pt, len: number): Pt => {
    const u = unit(from, to)
    return { x: from.x + u.x * len, y: from.y + u.y * len }
  }
  const stubOf = (p: Pt, q: Pt) => Math.min(STUB, dist(p, q) / 3)
  if (s === n - 1) {
    // The segment arrives at the end: keep a stub before it.
    const B = Q[n]!
    const T = along(B, Q[n - 1]!, stubOf(B, Q[n - 1]!))
    Q.splice(n, 0, { ...T }, { ...T })
  }
  if (s === 0) {
    // The segment leaves the start: keep a stub after it.
    const A = Q[0]!
    const S = along(A, Q[1]!, stubOf(A, Q[1]!))
    Q.splice(1, 0, { ...S }, { ...S })
    s = 2
  }
  const p = Q[s]!
  const q = Q[s + 1]!
  if (isLevel(p, q)) {
    p.y += by
    q.y += by
  } else {
    p.x += by
    q.x += by
  }
  return flatPts(tidy(Q).slice(1, -1))
}

/** Drop points that repeat or sit in the middle of a straight run. */
function tidy(pts: Pt[]): Pt[] {
  const out: Pt[] = []
  for (const p of pts) {
    const last = out[out.length - 1]
    if (last && Math.abs(last.x - p.x) < 0.5 && Math.abs(last.y - p.y) < 0.5) continue
    out.push(p)
  }
  for (let i = 1; i < out.length - 1; ) {
    const a = out[i - 1]!
    const b = out[i]!
    const c = out[i + 1]!
    if (Math.abs((b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x)) < 0.5) out.splice(i, 1)
    else i++
  }
  return out
}

/** A straight or curved line's bends with a new one put in on piece `i` (between bend i−1 and bend i), at `at`. */
export function addBend(line: El, i: number, at: Pt): number[] {
  const via = viaOf(line)
  via.splice(Math.max(0, Math.min(via.length, i)), 0, at)
  return flatPts(via)
}

/** A straight or curved line's bends with bend `i` moved to `at`. */
export function moveBend(line: El, i: number, at: Pt): number[] {
  const via = viaOf(line)
  if (i >= 0 && i < via.length) via[i] = at
  return flatPts(via)
}

/**
 * A straight or curved line's bends without those that lie in line with their neighbours (within `near` board pixels) —
 * a bend dragged back straight is gone, as in Miro. `ends`: where the line's two ends are drawn.
 */
export function straighten(via: Pt[], ends: [Pt, Pt], near: number): number[] {
  const all = [ends[0], ...via, ends[1]]
  for (let i = 1; i < all.length - 1; ) {
    const a = all[i - 1]!
    const b = all[i]!
    const c = all[i + 1]!
    const len = dist(a, c)
    const off = len ? Math.abs((c.x - a.x) * (a.y - b.y) - (a.x - b.x) * (c.y - a.y)) / len : dist(a, b)
    if (off <= near) all.splice(i, 1)
    else i++
  }
  return flatPts(all.slice(1, -1))
}

/** A line's bends moved with it by (dx, dy) — what a line carried along with its elements, or dragged whole, writes. */
export function shiftPts(line: El, dx: number, dy: number): number[] | null {
  const via = viaOf(line)
  if (!via.length) return null
  return flatPts(via.map((q) => ({ x: q.x + dx, y: q.y + dy })))
}

/** Is this line held at both ends by elements that are all moving — so its own bends must move with them? */
export function carriedBy(line: El, moving: Set<string>): boolean {
  return isAttached(line.a) && isAttached(line.b) && moving.has(line.a.id) && moving.has(line.b.id) && viaOf(line).length > 0
}

/** Where on an elbow a press landed: the segment nearest to it, or −1. */
export function nearestSegment(g: LineGeom, p: Pt): number {
  let best = Infinity
  let at = -1
  for (let i = 0; i + 1 < g.pts.length; i++) {
    const d = segDist(g.pts[i]!, g.pts[i + 1]!, p)
    if (d < best) {
      best = d
      at = i
    }
  }
  return at
}

/** Where on a straight or curved line a press landed: the piece (between two of its points) nearest to it. */
export function nearestPiece(g: LineGeom, p: Pt): number {
  if (!g.curve) return nearestSegment(g, p)
  let best = Infinity
  let at = 0
  for (let i = 0; i + 3 < g.pts.length; i += 3) {
    const piece = { ...g, pts: g.pts.slice(i, i + 4) }
    const pts = samples(piece)
    for (let k = 1; k < pts.length; k++) {
      const d = segDist(pts[k - 1]!, pts[k]!, p)
      if (d < best) {
        best = d
        at = i / 3
      }
    }
  }
  return at
}

function segDist(a: Pt, b: Pt, p: Pt): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const l2 = dx * dx + dy * dy
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

// ── hops ─────────────────────────────────────────────────────────────────────

/**
 * Where a line with `jump` hops over the lines drawn under it (Miro's "Line jumps"): the crossings of its straight runs
 * with theirs, by its segment. Curves neither hop nor are hopped (Miro's do; a curve's crossings are rare enough).
 */
export function hopsFor(line: El, g: LineGeom, under: LineGeom[]): Map<number, Pt[]> {
  const out = new Map<number, Pt[]>()
  if (!line.jump || g.curve) return out
  for (let i = 0; i + 1 < g.pts.length; i++) {
    const a = g.pts[i]!
    const b = g.pts[i + 1]!
    for (const o of under) {
      if (o.curve) continue
      for (let k = 0; k + 1 < o.pts.length; k++) {
        const x = crossing(a, b, o.pts[k]!, o.pts[k + 1]!)
        if (!x) continue
        // Not at a corner of either line: a hop there would sit on the round.
        const clear = CORNER + HOP
        if (dist(x, a) < clear || dist(x, b) < clear) continue
        const list = out.get(i) ?? []
        list.push(x)
        out.set(i, list)
      }
    }
  }
  return out
}

/** Where two segments cross inside both (not merely touching at an end), or null. */
function crossing(p1: Pt, p2: Pt, p3: Pt, p4: Pt): Pt | null {
  const d = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x)
  if (Math.abs(d) < 1e-9) return null
  const t = ((p3.x - p1.x) * (p4.y - p3.y) - (p3.y - p1.y) * (p4.x - p3.x)) / d
  const u = ((p3.x - p1.x) * (p2.y - p1.y) - (p3.y - p1.y) * (p2.x - p1.x)) / d
  const e = 1e-6
  if (t <= e || t >= 1 - e || u <= e || u >= 1 - e) return null
  return { x: p1.x + (p2.x - p1.x) * t, y: p1.y + (p2.y - p1.y) * t }
}
